import {
  equalTo,
  get,
  limitToLast,
  off,
  orderByChild,
  query,
  ref,
  update,
} from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole, renewToken } from "./auth";
import { LaunchError } from "../utils/errors";
import { CREATIVE_TEMPLATE_PRODUCE_STATUS } from "../utils/constants";
import { canWriteCreativeTemplates } from "../features/creativeTemplates/utils/permissions";
import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";

const getProducePath = ({ advertiserID, produceID }) =>
  `creativeTemplatesProduce/${advertiserID}/${produceID}`;

export const startGetCreativeTemplateProduce =
  ({ produceID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !produceID) return null;
      const snapshot = await get(
        ref(db, getProducePath({ advertiserID, produceID }))
      );
      return snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCreativeTemplateProduce",
          context: { produceID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return null;
    }
  };

export const startListenerCreativeTemplateProduce =
  ({
    produceID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !produceID) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(db, getProducePath({ advertiserID, produceID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.exists() ? snapshot.val() : null);
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeTemplateProduce",
          context: { produceID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return false;
    }
  };

const LAST_PRODUCES_LIMIT = 3;
const STALE_PROCESSING_MS = 60 * 60 * 1000;

// Devuelve las últimas producciones de la plantilla (más reciente primero).
export const startListLastCreativeTemplateProduces =
  ({ templateID, excludeProduceID = "" }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !templateID) return [];
      const snapshot = await get(
        query(
          ref(db, `creativeTemplatesProduce/${advertiserID}`),
          orderByChild("templateID"),
          equalTo(templateID),
          limitToLast(LAST_PRODUCES_LIMIT + 1)
        )
      );
      return Object.values(snapshot.val() || {})
        .filter((record) => record?.id && record.id !== excludeProduceID)
        .sort((left, right) => (right.updateTime || 0) - (left.updateTime || 0))
        .slice(0, LAST_PRODUCES_LIMIT);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListLastCreativeTemplateProduces",
          context: { templateID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return [];
    }
  };

export const startListenerTemplateActiveProduces =
  ({
    templateID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !templateID) {
        onUpdatedDataCallback(false);
        return false;
      }

      const dbQuery = query(
        ref(db, `creativeTemplatesProduce/${advertiserID}`),
        orderByChild("status"),
        equalTo(CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING)
      );
      onReferenceAvailable(dbQuery);
      onValue(dbQuery, (snapshot) => {
        const records = snapshot.val() || {};
        const now = Date.now();
        // Ignora registros "processing" abandonados para no bloquear
        // el guardado de la plantilla indefinidamente.
        onUpdatedDataCallback(
          Object.values(records).some(
            (record) =>
              record?.templateID === templateID &&
              now - (record.updateTime || record.creationTime || 0) <
                STALE_PROCESSING_MS
          )
        );
      });
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerTemplateActiveProduces",
          context: { templateID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeTemplateProduce =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startSaveCreativeTemplateProduce =
  ({
    produceID,
    templateID,
    language,
    rows,
    columns,
    itemCount,
    artboardIDs,
    artboardCount,
    variationCount,
    variationsPerRow,
    status,
    jobID,
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      if (!canWriteCreativeTemplates({ role })) {
        return { ok: false, reason: "unauthorized" };
      }
      if (!advertiserID || !produceID || !templateID) {
        return { ok: false, reason: "missingParameters" };
      }

      const now = Date.now();
      const path = getProducePath({ advertiserID, produceID });
      const snapshot = await get(ref(db, path));
      const existing = snapshot.val() || {};
      const creationTime = existing.creationTime || now;
      const resolvedArtboardCount = Number(artboardCount) || 1;
      const resolvedVariationsPerRow = Math.max(
        1,
        Number(variationsPerRow) || 1
      );
      const existingCreativeCount = Object.values(
        existing.creatives || {}
      ).filter((item) => item?.url).length;
      const parsedVariationCount = Number(variationCount);
      const updates = {
        [`${path}/id`]: produceID,
        [`${path}/advertiserID`]: advertiserID,
        [`${path}/templateID`]: templateID,
        [`${path}/language`]: language,
        [`${path}/rows`]: rows,
        [`${path}/itemCount`]: itemCount,
        [`${path}/artboardIDs`]: artboardIDs || null,
        [`${path}/artboardCount`]: resolvedArtboardCount,
        [`${path}/status`]:
          status || CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING,
        [`${path}/jobID`]: jobID || existing.jobID || null,
        [`${path}/error`]: null,
        [`${path}/summary`]: null,
        [`${path}/targetCreativeCount`]:
          existingCreativeCount +
          (Number(itemCount) || 0) *
            resolvedVariationsPerRow *
            resolvedArtboardCount,
        [`${path}/creationTime`]: creationTime,
        [`${path}/creationTimeNegative`]: -creationTime,
        [`${path}/updateTime`]: now,
        [`${path}/updateTimeNegative`]: -now,
      };
      if (Number.isInteger(parsedVariationCount) && parsedVariationCount > 0) {
        updates[`${path}/variationCount`] = parsedVariationCount;
      }
      if (resolvedVariationsPerRow > 0) {
        updates[`${path}/variationsPerRow`] = resolvedVariationsPerRow;
      }
      if (Array.isArray(columns)) {
        updates[`${path}/columns`] = columns.length > 0 ? columns : null;
      }

      await update(ref(db), updates);
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveCreativeTemplateProduce",
          context: { produceID, templateID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return { ok: false, reason: "saveFailed" };
    }
  };

export const startUpdateCreativeTemplateProduceStatus =
  ({ produceID, status, error = null }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !produceID || !status) {
        return { ok: false, reason: "missingParameters" };
      }
      const now = Date.now();
      const path = getProducePath({ advertiserID, produceID });
      await update(ref(db), {
        [`${path}/status`]: status,
        [`${path}/error`]: error || null,
        [`${path}/updateTime`]: now,
        [`${path}/updateTimeNegative`]: -now,
      });
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUpdateCreativeTemplateProduceStatus",
          context: { produceID, status },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startPrepareProduceRetry =
  ({ produceID, jobID, failedCount = 0 }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !produceID || !jobID) {
        return { ok: false, reason: "missingParameters" };
      }
      const now = Date.now();
      const path = getProducePath({ advertiserID, produceID });
      const snapshot = await get(ref(db, path));
      const existing = snapshot.val() || {};
      const existingCreativeCount = Object.values(
        existing.creatives || {}
      ).filter((item) => item?.url).length;
      const retryCount = Math.max(1, Number(failedCount) || 1);
      await update(ref(db), {
        [`${path}/status`]: CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING,
        [`${path}/error`]: null,
        [`${path}/summary`]: null,
        [`${path}/jobID`]: jobID,
        [`${path}/targetCreativeCount`]: existingCreativeCount + retryCount,
        [`${path}/updateTime`]: now,
        [`${path}/updateTimeNegative`]: -now,
      });
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startPrepareProduceRetry",
          context: { produceID, jobID, failedCount },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startSaveEditedProduceCreative =
  ({ produceID, creativeID, instance, url, width, height }) =>
  async (dispatch) => {
    try {
      const { role } = dispatch(getPathDatabaseByRole());
      if (!canWriteCreativeTemplates({ role })) {
        return { ok: false, reason: "unauthorized" };
      }
      const composedUrl = String(url || "").trim();
      if (!produceID || !creativeID || (!instance && !composedUrl)) {
        return { ok: false, reason: "missingParameters" };
      }
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "saveEditedProduceCreative"
        )}/saveEditedProduceCreative`,
        data: {
          produceID,
          creativeID,
          ...(instance ? { instance } : {}),
          ...(composedUrl ? { url: composedUrl } : {}),
          ...(Number(width) > 0 ? { width: Number(width) } : {}),
          ...(Number(height) > 0 ? { height: Number(height) } : {}),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!result?.ok) {
        return { ok: false, message: result?.message };
      }
      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveEditedProduceCreative",
          context: { produceID, creativeID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return { ok: false, reason: "saveFailed", message: error?.message };
    }
  };

export const startDeleteProduceCreatives =
  ({ produceID, creativeIDs = [] }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      if (!canWriteCreativeTemplates({ role })) {
        return { ok: false, reason: "unauthorized" };
      }
      const ids = [
        ...new Set(creativeIDs.map((id) => String(id || "").trim())),
      ].filter(Boolean);
      if (!advertiserID || !produceID || ids.length === 0) {
        return { ok: false, reason: "missingParameters" };
      }
      const path = getProducePath({ advertiserID, produceID });
      const now = Date.now();
      const updates = {
        [`${path}/updateTime`]: now,
        [`${path}/updateTimeNegative`]: -now,
      };
      ids.forEach((id) => {
        updates[`${path}/creatives/${id}`] = null;
      });
      await update(ref(db), updates);
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteProduceCreatives",
          context: { produceID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return { ok: false, reason: "deleteFailed" };
    }
  };

export const startRehostProduceRowImages =
  ({ urls = [] } = {}) =>
  async (dispatch) => {
    try {
      const { role } = dispatch(getPathDatabaseByRole());
      if (!canWriteCreativeTemplates({ role })) {
        return { ok: false, reason: "unauthorized", results: [] };
      }
      const uniqueUrls = [
        ...new Set(
          (Array.isArray(urls) ? urls : [])
            .map((url) => String(url || "").trim())
            .filter(Boolean)
        ),
      ];
      if (uniqueUrls.length === 0) {
        return { ok: true, results: [] };
      }
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "rehostProduceRowImages"
        )}/rehostProduceRowImages`,
        data: { urls: uniqueUrls },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!result?.ok) {
        return {
          ok: false,
          message: result?.message,
          results: result?.results || [],
        };
      }
      return { ok: true, results: result.results || [] };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startRehostProduceRowImages",
          context: { urlCount: Array.isArray(urls) ? urls.length : 0 },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return {
        ok: false,
        reason: "rehostFailed",
        message: error?.message,
        results: [],
      };
    }
  };
